import React, { useState } from "react";
import * as Icons from "lucide-react";
import {
  Archive,
  Edit,
  GripVertical,
  MoreVertical,
  Star,
  Trash,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Category } from "@/types/category";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  useDeleteCategory,
  useUpdateCategory,
} from "@/hooks/useCategories.hooks";
import { CategoryModal } from "./CategoryModal";
import { cn } from "@/lib/utils";

interface CategoryItemProps {
  category: Category;
  showPriority?: boolean;
  isActive?: boolean;
}

export const CategoryItem: React.FC<CategoryItemProps> = ({
  category,
  showPriority,
  isActive,
}) => {
  const { t } = useTranslation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const deleteCategory = useDeleteCategory();
  const updateCategory = useUpdateCategory();

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: "none",
    opacity: isDragging ? 0.5 : 1,
  };

  const IconComponent = category.icon ? (Icons as any)[category.icon] : null;

  const handleDelete = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (window.confirm(t("categories:deleteConfirmation"))) {
      deleteCategory.mutate(category.id);
    }
  };

  const handleArchive = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    updateCategory.mutate({
      id: category.id,
      data: { isArchived: !category.isArchived },
    });
  };

  const togglePriority = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateCategory.mutate({
      id: category.id,
      data: { isPriority: !category.isPriority },
    });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "border-base-border hover:bg-secondary-badge flex min-h-12 items-center gap-3 rounded-lg border bg-white px-3 py-2 transition-colors",
        category.isArchived && "opacity-50",
        isActive &&
          "border-custom-outline bg-secondary-badge hover:bg-secondary-badge",
      )}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab text-black/45 transition-colors hover:text-black/96"
      >
        <GripVertical className="size-4" />
      </div>

      <div className="border-base-border flex size-8 shrink-0 items-center justify-center rounded-md border bg-white text-black/65">
        {IconComponent ? (
          <IconComponent className="size-4" />
        ) : (
          <Icons.Layers className="size-4" />
        )}
      </div>

      <span
        className="line-clamp-2 min-w-0 flex-1 text-sm font-medium break-words text-black/96"
        title={category.name}
      >
        {category.name}
      </span>

      <div className="flex items-center gap-1">
        {showPriority && (
          <Button
            variant="new_ghost"
            size="icon_sm"
            onClick={togglePriority}
            className={cn(
              category.isPriority
                ? "text-royal-glow-3"
                : "text-muted-foreground",
            )}
            aria-label={t(
              category.isPriority
                ? "categories:removeFromPriority"
                : "categories:markAsPriority",
            )}
          >
            <Star fill={category.isPriority ? "currentColor" : "none"} />
          </Button>
        )}

        {category.isArchived ? (
          <Button
            variant="new_ghost"
            size="icon_sm"
            onClick={handleArchive}
            title={t("categories:restore")}
          >
            <Archive />
          </Button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="new_ghost"
                size="icon_sm"
                aria-label={t("categories:actions")}
              >
                <MoreVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuGroup>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditModalOpen(true);
                  }}
                >
                  <Edit />
                  <span>{t("categories:edit")}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleArchive}>
                  <Archive />
                  <span>{t("categories:archive")}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDelete} variant="destructive">
                  <Trash />
                  <span>{t("categories:delete")}</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <CategoryModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        category={category}
        entityType={category.entityType}
      />
    </div>
  );
};
